'use client';

import React, { useEffect, useRef } from 'react';
import Link from 'next/link';
import { X } from 'lucide-react';
import StatusBadge from '@/components/ui/StatusBadge';
import type { ReconRow } from '@/lib/reconciliation';
import { getRenormalisedWeights } from '@/lib/reconciliation';
import type { ReconStatus } from '@/lib/reconciliation-config';

import { useLanguage } from '@/context/LanguageContext';

interface ReconDrawerProps {
  row: ReconRow | null;
  open: boolean;
  onClose: () => void;
  weights: { primary: number; capacity: number; secondary: number };
  lagDays: number;
}

export default function ReconDrawer({ row, open, onClose, weights, lagDays }: ReconDrawerProps) {
  const { t, fmtNum } = useLanguage();
  const drawerRef = useRef<HTMLDivElement>(null);

  // Close on Escape
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && open) onClose();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open, onClose]);

  // Check prefers-reduced-motion
  const prefersReduced =
    typeof window !== 'undefined'
      ? window.matchMedia('(prefers-reduced-motion: reduce)').matches
      : false;

  const transitionStyle = prefersReduced
    ? {}
    : { transition: 'transform 250ms ease-out' };

  if (!row) return null;

  const normWeights = getRenormalisedWeights(row.expectedByClass, weights);

  const statusLabel = t(`status.${row.status}`);

  return (
    <>
      {/* Backdrop */}
      {open && (
        <div
          className="fixed inset-0 z-40"
          style={{ backgroundColor: 'rgba(10,40,32,0.35)' }}
          onClick={onClose}
          aria-hidden="true"
        />
      )}

      {/* Drawer */}
      <div
        ref={drawerRef}
        className="fixed top-0 right-0 h-full z-50 bg-surface-card border-l border-surface-border overflow-y-auto"
        style={{
          width: '460px',
          maxWidth: '100vw',
          transform: open ? 'translateX(0)' : 'translateX(100%)',
          ...transitionStyle,
          boxShadow: '-4px 0 24px rgba(0,0,0,0.12)',
        }}
        role="dialog"
        aria-modal="true"
        aria-label={`${row.contractor} reconciliation detail`}
      >
        {/* Header */}
        <div
          className="flex items-start justify-between p-5 border-b border-surface-border sticky top-0 bg-surface-card z-10"
        >
          <div className="min-w-0 flex-1">
            <h2 className="text-base font-bold text-text-main truncate">{row.contractor}</h2>
            <p className="text-xs text-text-muted mt-0.5">{row.region}</p>
            <div className="mt-2">
              <StatusBadge status={row.status as ReconStatus} label={statusLabel} />
            </div>
          </div>
          <button
            onClick={onClose}
            className="ml-3 p-1.5 rounded-lg hover:bg-surface-light transition-colors flex-shrink-0"
            aria-label="Close drawer"
          >
            <X size={16} className="text-text-muted" />
          </button>
        </div>

        <div className="p-5 space-y-6">
          {/* Blend Math */}
          <section>
            <h3 className="text-xs font-semibold uppercase tracking-wide text-text-muted mb-3">
              {t('recon.blendMath')}
            </h3>
            <div className="rounded-xl border border-surface-border overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr style={{ backgroundColor: 'var(--background)', borderBottom: '1px solid var(--border)' }}>
                    <th className="text-left py-2 px-3 text-xs font-semibold text-text-muted">Class</th>
                    <th className="text-right py-2 px-3 text-xs font-semibold text-text-muted">Subtotal</th>
                    <th className="text-right py-2 px-3 text-xs font-semibold text-text-muted">Weight</th>
                  </tr>
                </thead>
                <tbody>
                  {(['primary', 'capacity', 'secondary'] as const).map((cls) => {
                    const subtotal = row.expectedByClass[cls];
                    const weightPct = normWeights[cls];
                    if (subtotal === 0 && weightPct === 0) return null;
                    return (
                      <tr key={cls} style={{ borderBottom: '1px solid var(--border)' }}>
                        <td className="py-2 px-3 text-xs capitalize text-text-main">{cls}</td>
                        <td className="py-2 px-3 text-xs text-right tabular-nums text-text-main">
                          {fmtNum(Math.round(subtotal))}
                        </td>
                        <td className="py-2 px-3 text-xs text-right tabular-nums font-semibold"
                          style={{ color: 'var(--primary)' }}>
                          {weightPct}%
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr style={{ backgroundColor: 'var(--background)', borderTop: '1px solid var(--border)' }}>
                    <td className="py-2 px-3 text-xs font-bold text-text-main">Blended Expected</td>
                    <td className="py-2 px-3 text-xs text-right tabular-nums font-bold text-text-main" colSpan={2}>
                      {fmtNum(row.expectedConnections)}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </section>

          {/* Line Items */}
          <section>
            <h3 className="text-xs font-semibold uppercase tracking-wide text-text-muted mb-3">
              {t('recon.lineItems')}
            </h3>
            <div className="rounded-xl border border-surface-border overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr style={{ backgroundColor: 'var(--background)', borderBottom: '1px solid var(--border)' }}>
                    <th className="text-left py-2 px-3 text-xs font-semibold text-text-muted">ID</th>
                    <th className="text-left py-2 px-3 text-xs font-semibold text-text-muted">Item</th>
                    <th className="text-right py-2 px-3 text-xs font-semibold text-text-muted">Qty</th>
                    <th className="text-right py-2 px-3 text-xs font-semibold text-text-muted">{t('recon.ratioApplied')}</th>
                    <th className="text-right py-2 px-3 text-xs font-semibold text-text-muted">{t('recon.contribution')}</th>
                  </tr>
                </thead>
                <tbody>
                  {row.lineItems.map((li, i) => (
                    <tr key={i} style={{ borderBottom: '1px solid var(--border)' }}>
                      <td className="py-2 px-3">
                        <span className="font-mono text-[10px] text-text-muted">{li.moenId}</span>
                      </td>
                      <td className="py-2 px-3 text-xs text-text-main">
                        {t(`material.${li.itemCode}`) !== `material.${li.itemCode}` ? t(`material.${li.itemCode}`) : li.itemCode}
                      </td>
                      <td className="py-2 px-3 text-xs text-right tabular-nums text-text-main">
                        {fmtNum(li.quantity)}
                      </td>
                      <td className="py-2 px-3 text-xs text-right tabular-nums text-text-muted">
                        {li.ratio}
                      </td>
                      <td className="py-2 px-3 text-xs text-right tabular-nums font-medium"
                        style={{ color: 'var(--primary)' }}>
                        {fmtNum(Math.round(li.contribution))}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          {/* In-flight info */}
          {row.inFlightCount > 0 && (
            <section
              className="rounded-xl px-4 py-3 border text-xs text-text-muted"
              style={{ backgroundColor: 'rgba(201,151,58,0.06)', borderColor: 'rgba(201,151,58,0.2)' }}
            >
              <span className="font-semibold" style={{ color: 'var(--accent)' }}>
                {row.inFlightCount}
              </span>{' '}
              {t('recon.inFlight')} ({lagDays} {t('recon.days')})
            </section>
          )}

          {/* Linked Requisitions */}
          <section>
            <h3 className="text-xs font-semibold uppercase tracking-wide text-text-muted mb-2">
              {t('table.requisitionId')}
            </h3>
            <div className="flex flex-wrap gap-2">
              {row.requisitionIds.map((id) => (
                <Link
                  key={id}
                  href="/requisitions"
                  className="font-mono text-xs px-2 py-1 rounded-md border transition-colors hover:opacity-80"
                  style={{
                    backgroundColor: 'rgba(13,59,46,0.06)',
                    borderColor: 'rgba(13,59,46,0.15)',
                    color: 'var(--primary)',
                  }}
                >
                  {id}
                </Link>
              ))}
            </div>
          </section>
        </div>
      </div>
    </>
  );
}
